import Phaser from 'phaser';
import { GEODE_FRAME } from '../../../constants';

const MIN_BAKE_FRAME = 320;

export type SpritePackFrame = {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sourceX: number;
  sourceY: number;
};

export type SpritePack = {
  frames: SpritePackFrame[];
  sheet: HTMLImageElement;
  metaW: number;
};

type AsepriteJson = {
  frames: Record<
    string,
    {
      frame: { x: number; y: number; w: number; h: number };
      spriteSourceSize?: { x: number; y: number; w: number; h: number };
      sourceSize?: { w: number; h: number };
    }
  >;
  meta?: { size?: { w: number; h: number } };
};

type CompositeRect = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type BakeAtlasPlan = {
  startFrame: number;
  frameCount: number;
  cols: number;
  rows: number;
  width: number;
  height: number;
};

type BakePlan = {
  bakeFrame: number;
  frameStep: number;
  atlases: BakeAtlasPlan[];
};

export type BakedPlayerAtlas = {
  canvas: HTMLCanvasElement;
  textureKey: string;
  frameCount: number;
  cols: number;
  frameOffset: number;
};

export type BakedPlayerSheet = {
  atlases: BakedPlayerAtlas[];
  frameCount: number;
  frameStep: number;
  frameSize: number;
};

let cachedMaxGpuTexture = 0;

function detectMaxGpuTexture() {
  if (cachedMaxGpuTexture > 0) return cachedMaxGpuTexture;
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
  if (!gl) {
    cachedMaxGpuTexture = 4096;
    return cachedMaxGpuTexture;
  }
  cachedMaxGpuTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  return cachedMaxGpuTexture;
}

function maxBakeFrameSize(frameCount: number, maxGpuTexture: number) {
  for (let size = GEODE_FRAME; size >= MIN_BAKE_FRAME; size -= 1) {
    const cols = Math.max(1, Math.floor(maxGpuTexture / size));
    const maxRows = Math.max(1, Math.floor(maxGpuTexture / size));
    const framesPerAtlas = cols * maxRows;
    const atlasCount = Math.ceil(frameCount / framesPerAtlas);
    const widest = cols * size;
    let valid = widest <= maxGpuTexture;
    if (!valid) continue;
    for (let atlas = 0; atlas < atlasCount; atlas += 1) {
      const count = Math.min(framesPerAtlas, frameCount - atlas * framesPerAtlas);
      const rows = Math.ceil(count / cols);
      if (rows * size > maxGpuTexture) {
        valid = false;
        break;
      }
    }
    if (valid) return size;
  }
  return MIN_BAKE_FRAME;
}

function planBake(frameCount: number): BakePlan {
  const maxGpuTexture = detectMaxGpuTexture();
  const bakeFrame = maxBakeFrameSize(frameCount, maxGpuTexture);
  const cols = Math.max(1, Math.floor(maxGpuTexture / bakeFrame));
  const maxRows = Math.max(1, Math.floor(maxGpuTexture / bakeFrame));
  const framesPerAtlas = cols * maxRows;

  let frameStep = 1;
  while (frameStep <= frameCount) {
    const bakedCount = Math.ceil(frameCount / frameStep);
    const atlases: BakeAtlasPlan[] = [];
    let valid = true;

    for (let start = 0; start < bakedCount; start += framesPerAtlas) {
      const count = Math.min(framesPerAtlas, bakedCount - start);
      const rows = Math.ceil(count / cols);
      const width = cols * bakeFrame;
      const height = rows * bakeFrame;
      if (width > maxGpuTexture || height > maxGpuTexture) {
        valid = false;
        break;
      }
      atlases.push({ startFrame: start, frameCount: count, cols, rows, width, height });
    }

    if (valid) {
      return { bakeFrame, frameStep, atlases };
    }
    frameStep += 1;
  }

  return {
    bakeFrame: MIN_BAKE_FRAME,
    frameStep: frameCount,
    atlases: [{ startFrame: 0, frameCount: 1, cols: 1, rows: 1, width: MIN_BAKE_FRAME, height: MIN_BAKE_FRAME }],
  };
}

function frameSortIndex(name: string) {
  const match = name.match(/(\d+)(?:\.png)?$/i);
  return match ? parseInt(match[1], 10) : 0;
}

function configureBakeContext(ctx: CanvasRenderingContext2D) {
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
}

export async function loadSpritePack(jsonUrl: string, sheetUrl: string): Promise<SpritePack> {
  const jsonPath = encodeURI(jsonUrl);
  const sheetPath = encodeURI(sheetUrl);
  const [data, sheet] = await Promise.all([
    fetch(jsonPath).then((r) => {
      if (!r.ok) throw new Error(`Failed to load ${jsonPath}`);
      return r.json() as Promise<AsepriteJson>;
    }),
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load ${sheetPath}`));
      img.src = sheetPath;
    }),
  ]);

  const keys = Object.keys(data.frames).sort((a, b) => frameSortIndex(a) - frameSortIndex(b));

  const frames = keys.map((key) => {
    const entry = data.frames[key];
    const f = entry.frame;
    const source = entry.spriteSourceSize ?? { x: 0, y: 0, w: f.w, h: f.h };
    return {
      key,
      x: f.x,
      y: f.y,
      w: f.w,
      h: f.h,
      sourceX: source.x,
      sourceY: source.y,
    };
  });

  return {
    frames,
    sheet,
    metaW: data.meta?.size?.w ?? sheet.naturalWidth,
  };
}

function resolveLayerFrameIndex(pack: SpritePack, frameIndex: number) {
  return Math.min(frameIndex, pack.frames.length - 1);
}

function resolveSourceFrameCount(layerPacks: SpritePack[]) {
  const maxCount = Math.max(...layerPacks.map((p) => p.frames.length));
  const leader = layerPacks.find((p) => p.frames.length === maxCount);
  if (!leader || maxCount < 2) return maxCount;

  const first = leader.frames[0];
  const last = leader.frames[maxCount - 1];
  const duplicateTail =
    first.sourceX === last.sourceX &&
    first.sourceY === last.sourceY &&
    first.w === last.w &&
    first.h === last.h;

  return duplicateTail ? maxCount - 1 : maxCount;
}

function frameRect(pack: SpritePack, frameIndex: number): CompositeRect | null {
  const frame = pack.frames[resolveLayerFrameIndex(pack, frameIndex)];
  if (!frame) return null;
  return {
    minX: frame.sourceX,
    minY: frame.sourceY,
    maxX: frame.sourceX + frame.w,
    maxY: frame.sourceY + frame.h,
  };
}

function mergeRects(a: CompositeRect, b: CompositeRect): CompositeRect {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

function compositeRect(
  layerOrder: string[],
  packsByName: Record<string, SpritePack>,
  frameIndex: number,
): CompositeRect {
  let rect: CompositeRect | null = null;
  for (const name of layerOrder) {
    const next = frameRect(packsByName[name], frameIndex);
    if (!next) continue;
    rect = rect ? mergeRects(rect, next) : next;
  }
  return rect ?? { minX: 0, minY: 0, maxX: 1, maxY: 1 };
}

function globalCompositeRect(
  layerOrder: string[],
  packsByName: Record<string, SpritePack>,
  frameCount: number,
): CompositeRect {
  let rect: CompositeRect | null = null;
  for (let i = 0; i < frameCount; i++) {
    const next = compositeRect(layerOrder, packsByName, i);
    rect = rect ? mergeRects(rect, next) : next;
  }
  return rect ?? { minX: 0, minY: 0, maxX: 1, maxY: 1 };
}

function drawPackFrame(
  ctx: CanvasRenderingContext2D,
  pack: SpritePack,
  frameIndex: number,
  baseX: number,
  baseY: number,
  displayScale: number,
) {
  const frame = pack.frames[resolveLayerFrameIndex(pack, frameIndex)];
  if (!frame) return;
  const sheetScale = sheetScaleFactor(pack);
  const destX = baseX + frame.sourceX * displayScale;
  const destY = baseY + frame.sourceY * displayScale;
  ctx.drawImage(
    pack.sheet,
    frame.x * sheetScale,
    frame.y * sheetScale,
    frame.w * sheetScale,
    frame.h * sheetScale,
    destX,
    destY,
    frame.w * displayScale,
    frame.h * displayScale,
  );
}

function bakePlacement(globalRect: CompositeRect, bakeFrame: number) {
  const fitScale = bakeFrame / GEODE_FRAME;
  const baseX = (bakeFrame - GEODE_FRAME * fitScale) / 2;
  const baseY = bakeFrame - globalRect.maxY * fitScale;
  return { baseX, baseY, fitScale };
}

function sheetScaleFactor(pack: SpritePack) {
  return pack.sheet.naturalWidth / pack.metaW;
}

/** Merge layer packs frame-by-frame into one or more mobile-safe GPU textures. */
export function bakeMergedPlayerSheet(
  layerPacks: SpritePack[],
  layerOrder: string[],
  packsByName: Record<string, SpritePack>,
): BakedPlayerSheet {
  const sourceFrameCount = resolveSourceFrameCount(layerPacks);
  const plan = planBake(sourceFrameCount);
  const { bakeFrame, frameStep } = plan;
  const bakedFrameCount = Math.ceil(sourceFrameCount / frameStep);

  const globalRect = globalCompositeRect(layerOrder, packsByName, sourceFrameCount);
  const { baseX, baseY, fitScale } = bakePlacement(globalRect, bakeFrame);

  const scratch = document.createElement('canvas');
  scratch.width = bakeFrame;
  scratch.height = bakeFrame;
  const scratchCtx = scratch.getContext('2d');
  if (!scratchCtx) throw new Error('Canvas 2D unavailable');
  configureBakeContext(scratchCtx);

  const atlases: BakedPlayerAtlas[] = [];

  for (let atlasIndex = 0; atlasIndex < plan.atlases.length; atlasIndex += 1) {
    const atlasPlan = plan.atlases[atlasIndex];
    const canvas = document.createElement('canvas');
    canvas.width = atlasPlan.width;
    canvas.height = atlasPlan.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable');
    configureBakeContext(ctx);

    for (let localIndex = 0; localIndex < atlasPlan.frameCount; localIndex += 1) {
      const bakedIndex = atlasPlan.startFrame + localIndex;
      const sourceIndex = bakedIndex * frameStep;
      scratchCtx.clearRect(0, 0, bakeFrame, bakeFrame);
      for (const name of layerOrder) {
        const pack = packsByName[name];
        if (pack) drawPackFrame(scratchCtx, pack, sourceIndex, baseX, baseY, fitScale);
      }

      const col = localIndex % atlasPlan.cols;
      const row = Math.floor(localIndex / atlasPlan.cols);
      ctx.drawImage(scratch, col * bakeFrame, row * bakeFrame);
    }

    atlases.push({
      canvas,
      textureKey: `${PLAYER_COMPOSITE_KEY}-${atlasIndex}`,
      frameCount: atlasPlan.frameCount,
      cols: atlasPlan.cols,
      frameOffset: atlasPlan.startFrame,
    });
  }

  return {
    atlases,
    frameCount: bakedFrameCount,
    frameStep,
    frameSize: bakeFrame,
  };
}

export function registerBakedPlayerTexture(
  scene: Phaser.Scene,
  _textureKey: string,
  animKey: string,
  baked: BakedPlayerSheet,
  frameRate = 30,
) {
  removeBakedPlayerTextures(scene, animKey);

  const animFrames: { key: string; frame: string }[] = [];

  for (const atlas of baked.atlases) {
    scene.textures.addCanvas(atlas.textureKey, atlas.canvas);
    const texture = scene.textures.get(atlas.textureKey);
    texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
    texture.remove('__BASE');

    for (let i = 0; i < atlas.frameCount; i += 1) {
      const col = i % atlas.cols;
      const row = Math.floor(i / atlas.cols);
      const size = baked.frameSize;
      const frameName = String(atlas.frameOffset + i);
      texture.add(frameName, 0, col * size, row * size, size, size);
      animFrames.push({ key: atlas.textureKey, frame: frameName });
    }
  }

  if (animFrames.length <= 1) return;

  scene.anims.create({
    key: animKey,
    frames: animFrames,
    frameRate,
    repeat: -1,
  });
}

function removeBakedPlayerTextures(scene: Phaser.Scene, animKey: string) {
  if (scene.anims.exists(animKey)) scene.anims.remove(animKey);
  for (let i = 0; scene.textures.exists(atlasTextureKey(i)); i += 1) {
    scene.textures.remove(atlasTextureKey(i));
  }
}

function atlasTextureKey(index: number) {
  return `${PLAYER_COMPOSITE_KEY}-${index}`;
}

export function hasPlayerCompositeTexture(scene: Phaser.Scene) {
  return scene.textures.exists(atlasTextureKey(0));
}

export function removePlayerCompositeTexture(scene: Phaser.Scene) {
  removeBakedPlayerTextures(scene, PLAYER_COMPOSITE_ANIM);
}

export const PLAYER_COMPOSITE_KEY = 'player-standing-composite';
export const PLAYER_COMPOSITE_ANIM = 'player-standing-composite-anim';
export const PLAYER_COMPOSITE_TEXTURE = `${PLAYER_COMPOSITE_KEY}-0`;
